import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Table } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useMemo, useState } from "react";
import { useGetPriorityEntryCountQuery, useGetPriorityEntrySearchResultsQuery } from "../../graphql/graphql";
import { doesArrayHaveData } from "../../utils/Array.utils";
import { removeTypename } from "../../utils/Object.utils";
import { calculateOffset } from "../../utils/PageIndex.utils";
import { PriorityEntryEmptyState } from "../priority-entry/PriorityEntryEmptyState";
import { AutoHeightContainer } from "../../shared/Layout/AutoHeightContainer";
import { ManageProvidersForPatientModal } from "../patient/ManageProvidersForPatient";
import { PriorityReferralModal } from "../priority-referral/PriorityReferralModal";
import { TablePagination } from "../../shared/TablePagination";
import { PriorityEntryRow } from "../priority-entry/PriorityEntryRow";
import { ManagedTable } from "../../shared/ManagedTable";
import { useTherapproveDrawers } from "../../shared/TherapproveOverlays";
import { GlobalInviteModal } from "../global-invite/GlobalInviteModal";
export const PriorityViewTable = ({ search = "", priorityStatusList, isGlobalView = false, isActiveTab, readonly = false, options, containerStyle }) => {
    const { searchBody } = options ?? {};
    const cleanFilter = removeTypename(searchBody);
    const [filter, setFilter] = useState(cleanFilter);
    const [priorityEntryId, setPriorityEntryId] = useState(null);
    const [therapyCategoryId, setTherapyCategoryId] = useState(null);
    const { caregiver, patient, priorityEntry } = useTherapproveDrawers();
    // State
    const [page, setPage] = useState(1);
    // Effects — reset page when search changes
    useEffect(() => {
        setPage(1);
    }, [search]);
    // Get the priority entry search results
    const [{ data, fetching }] = useGetPriorityEntrySearchResultsQuery({
        requestPolicy: "cache-and-network",
        variables: {
            search: filter ?? {},
            matchTherapyCategory: true
        },
        pause: !filter || (isActiveTab !== undefined && !isActiveTab)
    });
    const searchResults = data?.priorityEntries?.priorityEntries;
    // Get the count of priority entries
    const [{ data: priorityEntryCountData }] = useGetPriorityEntryCountQuery({
        requestPolicy: "network-only",
        variables: {
            search: filter ?? {}
        },
        pause: !filter || (isActiveTab !== undefined && !isActiveTab)
    });
    const count = priorityEntryCountData?.priorityEntries?.priorityEntryCount ?? 0;
    // Priority Entry 'Release To Global' Modal
    const [globalInviteModalOpened, { open: globalInviteModalOpen, close: globalInviteModalClose }] = useDisclosure(false);
    // Priority Entry 'Referral' Modal
    const [priorityReferralModalOpened, { open: priorityReferralModalOpen, close: priorityReferralModalClose }] = useDisclosure(false);
    // Misc
    const [patientId, setPatientId] = useState("");
    // Assign Providers to Patient Modal
    const [manageProvidersForPatientModalOpened, { open: manageProvidersForPatientModalOpen, close: manageProvidersForPatientModalClose }] = useDisclosure(false);
    useEffect(() => {
        options?.setOpenModal?.(() => () => priorityEntry.open());
        // eslint-disable-next-line
    }, []);
    useEffect(() => {
        const cleanFilter = removeTypename(filter);
        setFilter({
            ...cleanFilter,
            where: {
                ...cleanFilter?.where,
                search: search
            },
            pagination: {
                offset: calculateOffset(page)
            }
        });
    }, [search, page]);
    useEffect(() => {
        const cleanFilter = removeTypename(searchBody);
        // Preserve the current search term when searchBody changes,
        // otherwise the searchBody effect wipes it on every re-render.
        setFilter((prev) => ({
            ...cleanFilter,
            where: {
                ...cleanFilter?.where,
                search: prev?.where?.search ?? search
            },
            pagination: prev?.pagination
        }));
    }, [searchBody]);
    // Open the Priority Entry 'Referral' Modal
    const openPriorityReferralModal = (priorityEntryId) => {
        setPriorityEntryId(priorityEntryId);
        priorityReferralModalOpen();
    };
    // Open the Caregiver Drawer — both profile and edit use the same drawer now
    const openCaregiverProfileDrawer = (caregiverId) => {
        caregiver.open({ caregiverId });
    };
    const openCaregiverDrawer = (caregiverId) => {
        caregiver.open({ caregiverId });
    };
    // Open the Patient Profile and Edit Drawers
    const openPatientProfileDrawer = (patientId, _canViewCaregiverDetails) => {
        patient.open({ patientId });
    };
    const openPatientDrawer = (patientId, _canEdit) => {
        patient.open({ patientId });
    };
    // Open the Assign Providers to Patient Modal
    const openManageProvidersForPatientModal = (patientId) => {
        setPatientId(patientId);
        manageProvidersForPatientModalOpen();
    };
    // Open the Priority Entry 'Release To Global' Modal
    const openGlobalInviteModal = (priorityEntryId, therapyCategoryId) => {
        setPriorityEntryId(priorityEntryId);
        setTherapyCategoryId(therapyCategoryId);
        globalInviteModalOpen();
    };
    /**
     * Create the table header
     */
    const tableHead = (_jsxs(Table.Tr, { children: [_jsx(Table.Th, { style: { width: "30px" } }), _jsx(Table.Th, { children: "Client" }), _jsx(Table.Th, { children: "Referral Stage" }), _jsx(Table.Th, { children: "Therapy Needed" }), !isGlobalView && _jsx(Table.Th, { children: "Assigned Providers" }), _jsx(Table.Th, { children: "Primary Contact" }), _jsx(Table.Th, { children: "Location" }), _jsx(Table.Th, { style: { width: "50px" } })] }));
    const priorityRows = useMemo(() => {
        if (!doesArrayHaveData(searchResults))
            return [];
        return searchResults.map((result, index) => {
            return (_jsx(PriorityEntryRow, { isGlobalView: isGlobalView, prioritySearchResult: result, priorityStatusList: priorityStatusList, modalHandlers: {
                    caregiverProfileDrawerOpen: openCaregiverProfileDrawer,
                    caregiverDrawerOpen: openCaregiverDrawer,
                    patientProfileDrawerOpen: openPatientProfileDrawer,
                    patientDrawerOpen: openPatientDrawer,
                    confirmInviteToGlobalQueueModal: openGlobalInviteModal,
                    manageProvidersForPatientModalOpen: openManageProvidersForPatientModal,
                    priorityReferralModalOpen: openPriorityReferralModal
                }, readonly: readonly }, `${result.priorityEntryId}-${index}`));
        });
    }, [searchResults, priorityStatusList, readonly]);
    const caption = _jsx(TablePagination, { count: count, page: page, setPage: setPage });
    return (_jsxs(AutoHeightContainer, { style: containerStyle, children: [_jsx(ManagedTable, { withBorder: true, header: tableHead, rows: priorityRows, caption: caption, minWidth: 800, fetching: fetching, emptyState: options?.emptyState ?? (_jsx(PriorityEntryEmptyState, { onAddPriorityEntry: () => priorityEntry.open() })) }), priorityEntryId && therapyCategoryId && (_jsx(GlobalInviteModal, { priorityEntryId: priorityEntryId, therapyCategoryIds: [therapyCategoryId], opened: globalInviteModalOpened, close: globalInviteModalClose })), patientId && manageProvidersForPatientModalOpened && (_jsx(ManageProvidersForPatientModal, { patientId: patientId, opened: manageProvidersForPatientModalOpened, close: manageProvidersForPatientModalClose })), priorityEntryId && (_jsx(PriorityReferralModal, { priorityEntryId: priorityEntryId, opened: priorityReferralModalOpened, close: priorityReferralModalClose }))] }));
};
